import i18next from "i18next";
import { TUnitTypeBackMdl, TUnitTypeListingMdl, TUnitTypeMdl } from "units/_models/UnitTypeMdl";
import { FEES, TFeature, TFees, TPropertyListingMdl } from "properties/_models/PropertyMdl";
import { TUnitMdl } from "admin/units/_models/UnitMdl";
import { TUnitListingMdl } from "admin/units/_models/UnitListingMdl";
import { flattenObject } from "_common/_utils/propertyUtils";
import { UNIT_CONVERSION } from "_common/_utils/unityUtils";

export function getTitleOfUnit(
    unit:
        | Pick<TUnitTypeBackMdl, "bedrooms" | "bathrooms" | "variant" | "type">
        | Pick<TUnitTypeMdl, "bedrooms" | "bathrooms" | "variant" | "type">,
) {
    if (!unit?.bedrooms || unit.bedrooms === 0) {
        return i18next.t(`unit.type.${unit.type}Studio`) + (unit?.variant ? ` - ${unit.variant}` : "");
    }
    return i18next.t(`unit.type.${unit.type}`, { count: unit.bedrooms }) + (unit?.variant ? ` - ${unit.variant}` : "");
}

export function isInPesosUnit(unit: { priceInPesos?: number }) {
    if (!unit || !unit?.priceInPesos) return false;
    return true;
}

export function getFees(propertyFees: TFees, unit: TUnitMdl | TUnitTypeListingMdl) {
    let feesValue = 0;
    if (propertyFees.type === FEES.noFees) return feesValue.toFixed(2);

    const totalFeesSurfaceProperty = propertyFees.surfaces?.reduce((acc, surface) => {
        const surfaceValue = unit?.[surface]?.min ?? unit?.[surface] ?? 0;
        return surfaceValue + acc;
    }, 0);

    if (propertyFees?.applyOnUnits) {
        feesValue = totalFeesSurfaceProperty
            ? totalFeesSurfaceProperty * (propertyFees?.value ?? 0)
            : propertyFees?.value ?? 0;
    } else {
        const totalFeesSurfaceUnit = unit?.fees?.surfaces?.reduce((acc, surface) => {
            const surfaceValue = unit?.[surface]?.min ?? unit?.[surface] ?? 0;
            return surfaceValue + acc;
        }, 0);
        feesValue = totalFeesSurfaceUnit
            ? totalFeesSurfaceUnit * (unit?.fees?.value ?? propertyFees?.value ?? 0)
            : totalFeesSurfaceProperty
            ? totalFeesSurfaceProperty * (unit?.fees?.value ?? propertyFees?.value ?? 0)
            : unit?.fees?.value ?? propertyFees?.value ?? 0;
    }
    return feesValue.toFixed(2);
}

export function getSurfacePriceSquareAndFt(priceSurface: number) {
    const surfaceInSquareFeet = priceSurface * UNIT_CONVERSION.M2_TO_FT2_BACK;
    return `US $ ${priceSurface}/m² / US $ ${surfaceInSquareFeet.toFixed(2)}/ft²`;
}

export function surface(surface: number) {
    const surfaceInSquareFeet = surface * UNIT_CONVERSION.M2_TO_FT2_BACK;
    return `${surface}m² / ${surfaceInSquareFeet.toFixed(2)}ft²`;
}

export function features(features: TFeature, translationKey = "property.features.") {
    return Object.entries(flattenObject(features))
        .filter(([_key, v]) => {
            return !!v;
        })
        .map(([key, value]) => {
            if (["privateTerrace", "privateTerrace"].includes(key)) {
                return i18next.t(`unit.features.nbOfFeatures.${key}`, { nb: value });
            }
            return i18next.t(`${translationKey}${key}`);
        })
        .sort();
}

export function getPropertyAmenities(item: TPropertyListingMdl) {
    const amenitiesEntries = Object.entries({ ...item.amenities });
    if (!amenitiesEntries.length) return [];

    return amenitiesEntries
        .map(([key, value]) => {
            if (!value) return;
            return i18next.t(`property.amenities.${key}`);
        })
        .filter((amenitie): amenitie is string => amenitie !== undefined);
}

export function parkings(parkings: { type: string; number: number }[]) {
    return parkings.map((parking) => {
        if (!parking.type) return parking.number;
        return i18next.t(`unit.parkingTypes.${parking.type}`, { count: parking.number });
    });
}

export function getUnitViews(item: TUnitListingMdl) {
    const viewsEntries = Object.entries({ ...item.views });
    if (!viewsEntries.length) return [];

    return viewsEntries
        .map(([key, value]) => {
            if (!value) return;
            return i18next.t(`unit.views.${key}`);
        })
        .filter((view): view is string => view !== undefined);
}
